import type { MaybeRef } from 'vue';
import { DOCUMENT } from '@pkg/config/sanity/schemas';
import { sanityImageWithAsset } from '@pkg/sanity-utils/sanityImageWithAsset';
import {
  absolutePageUrlFromPath,
  injectJsonLdScript,
  resolveSeoTitle,
  useJsonLdShared,
} from './useJsonLdShared';

interface PressNewsArticleContent {
  _type?: string;
  title?: string;
  excerpt?: string;
  publishedAt?: string;
  updatedAt?: string;
  _updatedAt?: string;
  seoSocial?: {
    metaTitle?: string;
    metaDescription?: string;
    socialImage?: any;
  };
  /** Sanity imageWithAlt — same builder input as author / SEO images */
  image?: any;
  slug?: string;
}

/**
 * NewsArticle JSON-LD for press release pages only.
 * Complements BreadcrumbList from usePostBreadcrumbJsonLd.
 *
 * Sanity fields: title, excerpt, publishedAt, updatedAt, _updatedAt, image (not mainImage).
 */
export function usePressNewsArticleJsonLd(
  content: MaybeRef<PressNewsArticleContent | null | undefined>,
) {
  const route = useRoute();
  const { $urlFor } = useNuxtApp();
  const { baseUrl, organizationId } = useJsonLdShared();

  const jsonLd = computed(() => {
    const press = unref(content);
    if (!press || press._type !== DOCUMENT.PRESS_RELEASES) {
      return null;
    }

    const headline = resolveSeoTitle(press);
    if (!headline) {
      return null;
    }

    const pageUrl = absolutePageUrlFromPath(baseUrl.value, route.path);
    if (!pageUrl) {
      return null;
    }
    const articleId = `${pageUrl}#article`;
    const webpageId = `${pageUrl}#webpage`;

    const description = (
      press.excerpt ||
      press.seoSocial?.metaDescription ||
      ''
    ).trim();

    const datePublished = press.publishedAt || undefined;
    const dateModified =
      press.updatedAt ||
      press._updatedAt ||
      press.publishedAt ||
      undefined;

    const imageSource = sanityImageWithAsset(press.image) ??
      sanityImageWithAsset(press.seoSocial?.socialImage);
    const imageUrl = imageSource
      ? $urlFor(imageSource).width(1200).fit('max').quality(90).url()
      : undefined;

    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': webpageId,
          url: pageUrl,
          name: headline,
          ...(description && { description }),
          mainEntity: { '@id': articleId },
        },
        {
          '@type': 'NewsArticle',
          '@id': articleId,
          headline,
          ...(description && { description }),
          ...(imageUrl && { image: imageUrl }),
          ...(datePublished && { datePublished }),
          ...(dateModified && { dateModified }),
          publisher: { '@id': organizationId.value },
          mainEntityOfPage: { '@id': webpageId },
        },
      ],
    };
  });

  injectJsonLdScript('press-newsarticle-jsonld', jsonLd);
}
